import { isGroupId } from '@core/group-refs';
import { MAX_GROUP_IDS } from '@core/types';
import { useEffect, useId, useRef, useState } from 'react';
import { api, ApiError, type GroupSummary } from '../api/client';

/**
 * Picks Entra groups by name so an administrator never has to paste a UUID.
 *
 * What is selected is always a list of **object IDs**. The display name travels alongside it purely
 * as a label: names in Entra are mutable and not unique, and the registry stores IDs for that
 * reason. Nothing in this component ever puts a displayName into the value it reports upward.
 *
 * A pasted object ID is still accepted, because operators have them in tickets and chat logs and
 * making them search for a group they can already identify exactly would be a step backwards.
 */

/** Long enough that a fast typist makes one request per word, short enough to feel immediate. */
const DEBOUNCE_MS = 250;
/** The server's floor. Below this, startswith() matches a large share of the tenant. */
const MIN_QUERY = 2;

export interface GroupPickerProps {
  /** Object IDs currently selected. The single source of truth; this component is controlled. */
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

type SearchState =
  | { kind: 'idle' }
  | { kind: 'searching' }
  | { kind: 'results'; groups: GroupSummary[] }
  /**
   * Held separately from an empty result set on purpose. Rendering a failed lookup as "no groups
   * found" would tell the operator their spelling was wrong when the directory was unreachable.
   */
  | { kind: 'failed'; message: string };

export function GroupPicker({ value, onChange, disabled }: GroupPickerProps) {
  const [query, setQuery] = useState('');
  const [state, setState] = useState<SearchState>({ kind: 'idle' });
  const [active, setActive] = useState(0);
  /**
   * What we know about each selected ID, learned from a search result or from the resolve call.
   *
   *   string    - the group's display name.
   *   null      - Graph answered and no group has this ID. Deleted in Entra, almost certainly.
   *   undefined - not looked up, or the lookup failed. Show the bare UUID and claim nothing.
   *
   * A label, never an input. The value reported upward is `value`, which holds only IDs.
   */
  const [names, setNames] = useState<Record<string, string | null>>({});
  /** Set when a resolve call failed, so the UI can say "unlabelled" rather than "deleted". */
  const [resolveFailed, setResolveFailed] = useState(false);

  const listId = useId();
  const optionId = (index: number) => `${listId}-option-${index}`;

  /**
   * IDs already sent to the resolve endpoint, so opening a form does not re-request on every
   * render. A failed attempt is removed again, letting the next change retry.
   */
  const attempted = useRef(new Set<string>());

  /**
   * Labels the groups an app is already gated on, as soon as the form appears.
   *
   * Without this, a saved app shows a column of UUIDs until the operator happens to search for the
   * very name they are looking at. A pasted ID lands here too: `add` records no name for it, so it
   * arrives unattempted and gets labelled on the next pass.
   *
   * Deliberately not blocking. The form is fully usable while this is in flight and stays usable if
   * it fails - the IDs are what will be saved either way, and nothing here gates submission.
   */
  useEffect(() => {
    const missing = value.filter((id) => !attempted.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => attempted.current.add(id));

    let cancelled = false;
    api
      .resolveGroups(missing)
      .then((resolved) => {
        if (cancelled) return;
        setResolveFailed(false);
        setNames((prev) => ({
          ...prev,
          // Default every ID we asked about to null, then overlay whatever came back. The route's
          // contract is to answer for all of them, but depending on that silently is what would
          // make the "not in Entra" marker quietly stop appearing if the contract ever changed -
          // and that marker exists precisely to stop a broken app going unnoticed. An ID that was
          // asked about and did not come back means the same thing as one that came back null.
          ...Object.fromEntries(missing.map((id) => [id, null])),
          ...Object.fromEntries(resolved.map((g) => [g.id, g.displayName])),
        }));
      })
      .catch(() => {
        if (cancelled) return;
        // Leave the UUIDs unlabelled rather than marking them deleted, and allow a later retry.
        // Calling these "not found in Entra" on a transient outage would be a false alarm about
        // an app's access being broken.
        missing.forEach((id) => attempted.current.delete(id));
        setResolveFailed(true);
      });

    return () => {
      cancelled = true;
    };
    // Keyed on the contents, not the array identity, so a re-render with an equal list is a no-op.
  }, [value.join(',')]);

  // Only the newest query may write state. Without this, a slow request for "pay" landing after a
  // fast one for "payroll" would replace the right results with stale ones.
  const latest = useRef(0);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY) {
      setState({ kind: 'idle' });
      return;
    }
    // A pasted UUID is handled by the Add button, not by asking Graph to prefix-match a UUID.
    if (isGroupId(trimmed)) {
      setState({ kind: 'idle' });
      return;
    }

    const seq = ++latest.current;
    setState({ kind: 'searching' });

    const timer = setTimeout(() => {
      api
        .searchGroups(trimmed)
        .then((groups) => {
          if (seq !== latest.current) return;
          setState({ kind: 'results', groups });
          setActive(0);
          setNames((prev) => ({
            ...prev,
            ...Object.fromEntries(groups.map((g) => [g.id, g.displayName])),
          }));
        })
        .catch((err) => {
          if (seq !== latest.current) return;
          setState({
            kind: 'failed',
            message:
              err instanceof ApiError
                ? err.message
                : 'The group lookup failed. You can paste a group object ID instead.',
          });
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  const atCap = value.length >= MAX_GROUP_IDS;

  function add(id: string, displayName?: string): void {
    const normalized = id.trim().toLowerCase();
    // Silently ignoring a duplicate rather than erroring: the operator's intent is satisfied
    // either way, and normalizeGroupIds on the server dedupes regardless.
    if (value.includes(normalized) || atCap) return;
    if (displayName) {
      // Picked from a search result, so the name is already known: record it and mark the ID
      // attempted so the resolve effect does not spend a request re-learning it. A pasted ID has
      // no name here, stays unattempted, and is labelled by that effect instead.
      setNames((prev) => ({ ...prev, [normalized]: displayName }));
      attempted.current.add(normalized);
    }
    onChange([...value, normalized]);
    setQuery('');
    setState({ kind: 'idle' });
  }

  function remove(id: string): void {
    onChange(value.filter((v) => v !== id));
  }

  const results = state.kind === 'results' ? state.groups : [];
  const pastedId = isGroupId(query) ? query.trim().toLowerCase() : undefined;

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (pastedId && event.key === 'Enter') {
      event.preventDefault();
      add(pastedId);
      return;
    }
    if (results.length === 0) return;

    // Arrow keys move the active option; Enter takes it. Without this the list is mouse-only, which
    // makes the picker unusable for anyone navigating by keyboard.
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive((i) => (i + 1) % results.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive((i) => (i - 1 + results.length) % results.length);
        break;
      case 'Enter': {
        event.preventDefault();
        const chosen = results[active];
        if (chosen) add(chosen.id, chosen.displayName);
        break;
      }
      case 'Escape':
        setState({ kind: 'idle' });
        break;
    }
  }

  return (
    <div className="field">
      <span id={`${listId}-label`}>Required Entra groups</span>

      {value.length > 0 && (
        <ul className="chips plain">
          {value.map((id) => {
            const name = names[id];
            const named = typeof name === 'string';
            // Resolved, and Entra has no such group. Worth saying out loud: the trigger still
            // compares against this UUID, so the app is gated on a group nobody can be in.
            const orphaned = name === null;
            return (
              <li key={id} className={orphaned ? 'chip chip-orphaned' : 'chip'}>
                {/* The name labels the chip; the ID is what is stored, so it stays on screen
                    rather than hiding behind a tooltip. */}
                <span className="chip-name">{named ? name : id}</span>
                {named && <code className="chip-id">{id}</code>}
                {orphaned && (
                  <span className="tag tag-warn" title="No group with this ID exists in Entra">
                    not in Entra
                  </span>
                )}
                <button
                  type="button"
                  className="chip-remove"
                  onClick={() => remove(id)}
                  disabled={disabled}
                  aria-label={`Remove ${named ? name : id}`}
                  title={id}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/*
        A hint, not an error. The IDs above are exactly what will be saved whether or not we could
        label them, so this must not read as something the operator has to fix before continuing.
      */}
      {resolveFailed && value.length > 0 && (
        <span className="hint">
          Could not reach Entra to look up these group names, so they are shown as object IDs. The
          groups themselves are unaffected and saving still works.
        </span>
      )}

      <div className="picker">
        <input
          type="text"
          role="combobox"
          aria-expanded={results.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={results.length > 0 ? optionId(active) : undefined}
          aria-labelledby={`${listId}-label`}
          autoComplete="off"
          value={query}
          disabled={disabled || atCap}
          placeholder={atCap ? `At most ${MAX_GROUP_IDS} groups` : 'Search by group name, or paste an object ID'}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />

        {pastedId && !value.includes(pastedId) && (
          <button type="button" onClick={() => add(pastedId)} disabled={disabled || atCap}>
            Add this ID
          </button>
        )}
      </div>

      {state.kind === 'searching' && <span className="hint">Searching Entra…</span>}

      {/* A failed lookup is an error, never an empty list. See the SearchState comment. */}
      {state.kind === 'failed' && (
        <div className="callout callout-error" role="alert">
          {state.message}
        </div>
      )}

      {state.kind === 'results' && results.length === 0 && (
        <span className="hint">
          No Entra group starts with “{query.trim()}”. Check the spelling, or paste the object ID.
        </span>
      )}

      {results.length > 0 && (
        <ul className="results plain" id={listId} role="listbox" aria-labelledby={`${listId}-label`}>
          {results.map((group, index) => {
            const already = value.includes(group.id);
            return (
              <li key={group.id} id={optionId(index)} role="option" aria-selected={index === active}>
                <button
                  type="button"
                  className={index === active ? 'result active' : 'result'}
                  disabled={disabled || already || atCap}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => add(group.id, group.displayName)}
                >
                  <span className="result-name">{group.displayName}</span>
                  <code className="result-id">{group.id}</code>
                  {already && <span className="tag">already added</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <span className="hint">
        Search by name and pick from the list, or paste a group object ID. At most {MAX_GROUP_IDS}.
        Leave empty to allow any authenticated user.
      </span>
    </div>
  );
}
